import { Joinability, sortTablesByJoinability } from './cashStakeOrder';
import type { SitGo } from './types';
import { SEATS_BY_FORMAT } from './useLobbyFilters';

/**
 * EN-11139 AC 2-5 (release.sit-go-buy-in-lobby): the Sit & Go tab read like
 * the cash tab — one section per buy-in instead of the prize-pool ladder.
 */

// Heads-up before six-seat inside every group (AC 3). Read when called, not at
// import: lobby tests stub useLobbyFilters.
const sitGoFormatOrder = (): readonly number[] => [
  SEATS_BY_FORMAT.headsUp,
  SEATS_BY_FORMAT.sixSeat
];

// Partially filled, then the empty ones, then the full ones (AC 3). A Sit &
// Go has no waitlist, so the shared rule's 'queue' group is its full tables.
const SIT_GO_GROUP_ORDER: readonly Joinability[] = ['open', 'empty', 'queue'];

// The trim's template identity (AC 4). A row without a template key (a test
// fixture, a row the adapter did not build) trims by variant, game and seats.
const sitGoTemplateOf = (game: SitGo): string =>
  JSON.stringify([game.variant, game.game ?? '', game.templateKey ?? '', game.capacity]);

/**
 * One section's tables, left to right: partial heads-up, partial six-seat,
 * one empty heads-up, one empty six-seat, full heads-up, full six-seat; ties
 * by table id — EN-11152's sortTablesByJoinability with the Sit & Go group
 * order, so one rule serves both tabs.
 *
 * Exactly one empty table per template (AC 4): the lowest id is kept and any
 * other empty table of that template is left out, so a section never shows a
 * row of identical empty seats. A template is what the server keeps one
 * empty table of (sitAndGoEmptyTable.js), as far as the lobby row carries it:
 * the variant and game, the template key (adaptSitGo) and the seat count.
 * Today each format has one template per price, so that is one empty table
 * per format; a second template at the same price and seats (PLO beside NLH,
 * another structure) keeps its own.
 *
 * The player's own table is not lifted to the front, as the ladder's sort
 * did: the ticket gives the order exactly, the cash tab has no such rule, and
 * a table the player has registered for is partially filled — the first
 * group — until it starts; the glow still marks it.
 */
export const orderSitGoSection = (games: readonly SitGo[]): SitGo[] => {
  const sorted = sortTablesByJoinability(
    games.map((game) => ({
      game,
      tableId: game.tableId,
      seated: game.registered,
      capacity: game.capacity
    })),
    (row) => row.capacity,
    sitGoFormatOrder(),
    SIT_GO_GROUP_ORDER
  );
  const emptyShown = new Set<string>();
  return sorted
    .filter((row) => {
      if (row.seated > 0) return true;
      const template = sitGoTemplateOf(row.game);
      if (emptyShown.has(template)) return false;
      emptyShown.add(template);
      return true;
    })
    .map((row) => row.game);
};

/** One section per buy-in (stake + fee, as the engine broadcasts it), ascending. */
export const groupSitGosByBuyIn = (games: readonly SitGo[]): Array<readonly [number, SitGo[]]> => {
  const byBuyIn = new Map<number, SitGo[]>();
  games.forEach((game) => {
    const list = byBuyIn.get(game.buyIn) ?? [];
    list.push(game);
    byBuyIn.set(game.buyIn, list);
  });
  return Array.from(byBuyIn.entries())
    .sort(([a], [b]) => a - b)
    .map(([buyIn, list]) => [buyIn, orderSitGoSection(list)] as const);
};

/**
 * The section heading: just the amount, in full dollars ("$6", "$110",
 * "$1,100"), the way the cash heading is just the blinds. Cents only when
 * there are some.
 */
export const formatBuyInHeading = (buyIn: number): string => {
  const amount = Number.isFinite(buyIn) ? buyIn : 0;
  const cents = !Number.isInteger(amount);
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0
  })}`;
};

/** The card's name slot (AC 5): the format, or the template's own name. */
export const sitGoCardName = (game: Pick<SitGo, 'capacity' | 'name'>): string => {
  if (game.capacity === SEATS_BY_FORMAT.headsUp) return 'Heads-up';
  if (game.capacity === SEATS_BY_FORMAT.sixSeat) return 'Six-seat';
  return game.name;
};
